import type { ScopeOfWorkData } from "@rebuild/shared";
import { formatPrice } from "@rebuild/shared";
import type { useInlineEditing } from "../hooks/useInlineEditing";

type InlineEditingReturn = ReturnType<typeof useInlineEditing>;

interface PDFRoomGroupedEditorProps {
  editableScopeOfWork: ScopeOfWorkData & { type: "room-grouped" };
  inlineEditing: InlineEditingReturn;
  includeQuantity: boolean;
}

export function PDFRoomGroupedEditor({ editableScopeOfWork, inlineEditing, includeQuantity }: PDFRoomGroupedEditorProps) {
  const { editingValue, setEditingValue, startEditing, saveEdit, cancelEdit, isEditing } = inlineEditing;
  const hideLineItemPrices = editableScopeOfWork.hideLineItemPrices === true;

  return (
    <div className="pl-4 text-sm leading-relaxed">
      {editableScopeOfWork.scopeOfWorkRooms.map((room, roomIndex) => (
        <div key={roomIndex} className="mb-4">
          {/* Room header with price */}
          <div className="mb-2 flex items-center justify-between">
            {isEditing("room", "text", roomIndex) ? (
              <input
                type="text"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={saveEdit}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit();
                  if (e.key === "Escape") cancelEdit();
                }}
                autoFocus
                className="mr-2 flex-1 rounded border border-blue-500 px-1 font-semibold outline-none"
              />
            ) : (
              <div
                className="-mx-1 cursor-pointer rounded px-1 font-semibold hover:bg-gray-100"
                onClick={() => startEditing("room", "text", roomIndex, undefined, undefined, room.room)}
              >
                {room.room}
              </div>
            )}
            <div
              className="group relative -mx-1 rounded px-1 font-semibold"
              title={hideLineItemPrices ? "Edit individual category prices to update this total" : "Only edit individual line item prices"}
            >
              {formatPrice(room.price)}
              <div className="absolute right-0 bottom-full z-10 mb-2 hidden rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
                {hideLineItemPrices ? "Edit individual category prices to update this total" : "Only edit individual line item prices"}
              </div>
            </div>
          </div>
          {/* Horizontal line under room header */}
          <hr className="mb-2 border-t border-gray-300" />
          {room.categories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-2 ml-4">
              {/* Category header with price */}
              <div className="mb-2 flex items-center justify-between">
                {isEditing("category", "text", roomIndex, categoryIndex) ? (
                  <input
                    type="text"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    onBlur={saveEdit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") cancelEdit();
                    }}
                    autoFocus
                    className="mr-2 flex-1 rounded border border-blue-500 px-1 font-medium outline-none"
                  />
                ) : (
                  <div
                    className="-mx-1 cursor-pointer rounded px-1 font-medium hover:bg-gray-100"
                    onClick={() => startEditing("category", "text", roomIndex, categoryIndex, undefined, category.category)}
                  >
                    {category.category}
                  </div>
                )}
                {hideLineItemPrices ? (
                  isEditing("category", "price", roomIndex, categoryIndex) ? (
                    <div className="flex items-center">
                      <span className="mr-1">$</span>
                      <input
                        type="number"
                        step="0.01"
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onBlur={saveEdit}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveEdit();
                          if (e.key === "Escape") cancelEdit();
                        }}
                        autoFocus
                        className="w-24 rounded border border-blue-500 px-1 text-right font-medium outline-none"
                      />
                    </div>
                  ) : (
                    <div
                      className="-mx-1 cursor-pointer rounded px-1 font-medium hover:bg-gray-100"
                      onClick={() => startEditing("category", "price", roomIndex, categoryIndex, undefined, category.price)}
                    >
                      {formatPrice(category.price)}
                    </div>
                  )
                ) : (
                  <div className="group relative -mx-1 rounded px-1 font-medium" title="Only edit individual line item prices">
                    {formatPrice(category.price)}
                    <div className="absolute right-0 bottom-full z-10 mb-2 hidden rounded bg-gray-800 px-2 py-1 text-xs whitespace-nowrap text-white group-hover:block">
                      Only edit individual line item prices
                    </div>
                  </div>
                )}
              </div>
              <div className="ml-4">
                {category.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className={`flex items-center justify-between ${itemIndex === category.items.length - 1 ? "mb-2" : "mb-1"}`}
                  >
                    <div className="flex w-[75%] items-center justify-between">
                      {isEditing("item", "text", roomIndex, categoryIndex, itemIndex) ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={saveEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveEdit();
                            if (e.key === "Escape") cancelEdit();
                          }}
                          autoFocus
                          className="mr-2 flex-1 rounded border border-blue-500 px-1 outline-none"
                        />
                      ) : (
                        <div
                          className="-mx-1 cursor-pointer rounded px-1 hover:bg-gray-100"
                          onClick={() => startEditing("item", "text", roomIndex, categoryIndex, itemIndex, item.item)}
                        >
                          - {item.item}
                        </div>
                      )}
                    </div>
                    <div className="flex w-[22%] items-center justify-between">
                      {includeQuantity ? (
                        <>
                          {isEditing("item", "quantity", roomIndex, categoryIndex, itemIndex) ? (
                            <input
                              type="number"
                              value={editingValue}
                              onChange={(e) => setEditingValue(e.target.value)}
                              onBlur={saveEdit}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveEdit();
                                if (e.key === "Escape") cancelEdit();
                              }}
                              autoFocus
                              className="mr-2 w-[20px] flex-1 rounded border border-blue-500 px-1 outline-none"
                            />
                          ) : (
                            <div
                              className="-mx-1 cursor-pointer rounded px-1 hover:bg-gray-100"
                              onClick={() => startEditing("item", "quantity", roomIndex, categoryIndex, itemIndex, item.quantityValue)}
                            >
                              {item.quantityValue} {item.quantityUnit.toUpperCase()}
                            </div>
                          )}
                        </>
                      ) : (
                        <div />
                      )}

                      {hideLineItemPrices ? null : isEditing("item", "price", roomIndex, categoryIndex, itemIndex) ? (
                        <div className="flex items-center">
                          <span className="mr-1">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={saveEdit}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit();
                              if (e.key === "Escape") cancelEdit();
                            }}
                            autoFocus
                            className="w-24 rounded border border-blue-500 px-1 text-right outline-none"
                          />
                        </div>
                      ) : (
                        <div
                          className="-mx-1 cursor-pointer rounded px-1 hover:bg-gray-100"
                          onClick={() => startEditing("item", "price", roomIndex, categoryIndex, itemIndex, item.price)}
                        >
                          {formatPrice(item.price)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
